import { useState, useEffect, useMemo, useRef } from 'react';
import { LayoutGrid, ScanLine, X, Loader2, MapPin, Camera, CornerDownLeft, AlertTriangle } from 'lucide-react';
import CameraCapture from '../components/CameraCapture';
import { matchStreet, streetCanon, looksLikeStreet } from '../utils/streetMatch';

// ── Chute Layout (sorter portal) ───────────────────────────────────────────────
// A visual sort frame per chute, built live from the sort-guide route data (same feed
// as the Sort Guide tab), plus a scan-a-label flow that OCRs a parcel address and tells
// the sorter which outlet / chute / bay it drops to — with number-range logic when a
// street is split across drivers.
//
// Geometry is DATA-DRIVEN: an outlet's number decides its position on the frame
// (>=19 → left column, <=14 → right column, 15–18 → top pair, >=40 → overflow), matching
// the physical Chute 6 diagram. Per-chute config only carries the physical differences.

const API = '/.netlify/functions/appsscript';
function callAction(action, payload) {
  return fetch(API, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, ...(payload || {}) }),
  }).then(r => r.json());
}

// Physical differences between the three chutes we sort here.
const CHUTES = [
  { id: '5',  label: 'Chute 5',  sides: 'both', walkways: false, blurb: 'City drivers' },
  { id: '6',  label: 'Chute 6',  sides: 'both', walkways: true,  blurb: 'Darlinghurst + 2 City' },
  { id: '15', label: 'Chute 15', sides: 'left', walkways: false, blurb: 'Broadway — half chute', otherOperator: true },
];

// ── street / number-range helpers (same rules as the app's route search) ────────
// A house-number range: "400-420", "424 - 428", and "180.-234" (AP data carries a stray
// dot after the first number) — the dot/comma before the dash is tolerated.
const RANGE_RX = () => /(\d+)\s*[.,]?\s*[-–]\s*(\d+)/g;
function extractRanges(entry) {
  const r = [], rx = RANGE_RX(); let m;
  while ((m = rx.exec(entry)) !== null) r.push([+m[1], +m[2]]);
  return r;
}
// Numeric coverage a route entry claims: ranges ("400 - 420") AND bare specific numbers
// ("95 PITT ST", "93, 95 LIVERPOOL ST") as exact [n,n] points. A single number matches
// only itself — it is NOT a catch-all. Only a digit-less entry claims nothing.
function entryBands(entry) {
  const bands = extractRanges(entry);
  const rest = String(entry).replace(RANGE_RX(), ' ');
  const rx = /\d+/g; let m;
  while ((m = rx.exec(rest)) !== null) bands.push([+m[0], +m[0]]);
  return bands;
}
function inRange(num, lo, hi) {
  const mn = Math.min(lo, hi), mx = Math.max(lo, hi);
  if (num < mn || num > mx) return false;
  const loOdd = lo % 2 !== 0, hiOdd = hi % 2 !== 0;
  if (loOdd === hiOdd) return (num % 2 !== 0) === loOdd; // matched-parity band (odd side / even side)
  return true;                                           // mixed parity → whole run
}
// Human-readable street name for DISPLAY (keeps case): drop the number/range only.
const streetNameOf = entry => String(entry).replace(RANGE_RX(), '').replace(/[.,]/g, ' ').replace(/\s+/g, ' ').trim();
// Which route entries name the street we're resolving? Street identity lives in
// utils/streetMatch — name words AND type must agree, so "Bridge Road" no longer
// resolves to "Pyrmont bridge rd" (a different street on a different round) the
// way plain substring matching did. Prose on a route list ("Loading Bay Dawson
// Street", "Broadway shopping centre Bay st") still matches the street it wraps.
const sameStreetAs = (entry, street) => matchStreet(entry, street) !== null;
const outletNum = rt => { const m = String(rt.outlet || '').match(/\d+/); return m ? parseInt(m[0], 10) : null; };
const isMissort = rt => /missort/i.test(rt.login || '') || (!rt.area && !rt.bay);

// ── colours (borrow the Sort Guide legend) ─────────────────────────────────────
const C = {
  yellow: '#c2d100', yellowInk: '#262a00',
  gold: 'var(--hp-gold)', sky: 'var(--hp-sky, #38BDF8)', pink: 'var(--hp-pink, #EC4899)',
  dim: 'var(--hp-dim)', border: 'var(--hp-border)', surface: 'var(--hp-surface)',
  card: 'var(--hp-card, #1E1E24)', text: 'var(--hp-text)',
};
const card = { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 'var(--radius-lg, 14px)', padding: '18px 16px', marginBottom: 16 };
const title = { fontFamily: 'var(--font-display)', fontSize: '1.15rem', letterSpacing: '0.03em', margin: '0 0 4px' };
const sub = { fontSize: '0.82rem', color: C.dim, margin: '0 0 14px', lineHeight: 1.5 };

// ── build a frame's cells from the routes on a chute ────────────────────────────
function columnCells(items, { walkways, desc }) {
  if (!items.length) return [];
  const sorted = [...items].sort((a, b) => a.n - b.n);
  if (!walkways) {
    const arr = sorted.map(it => ({ type: 'outlet', it }));
    return desc ? arr.reverse() : arr;
  }
  const byN = new Map(sorted.map(it => [it.n, it]));
  const cells = [];
  for (let n = sorted[0].n; n <= sorted[sorted.length - 1].n; n++) {
    cells.push(byN.has(n) ? { type: 'outlet', it: byN.get(n) } : { type: 'walk', n });
  }
  return desc ? cells.reverse() : cells;
}

function buildFrame(routes, cfg) {
  const rs = (routes || []).filter(r => String(r.chute) === cfg.id);
  // Bucket each round by its outlet number's physical position on the frame.
  const t2 = [], l2 = [], r2 = [], o2 = [], unplaced = [];
  rs.forEach(rt => {
    const n = outletNum(rt);
    if (n == null) { unplaced.push(rt); return; }
    const it = { n, rt };
    if (n >= 40) o2.push(it);        // overflow (e.g. 100)
    else if (n >= 19) l2.push(it);   // left column
    else if (n >= 15) t2.push(it);   // top pair (15–18)
    else r2.push(it);                // right column (≤14)
  });

  if (cfg.sides === 'left') {
    // one column: top outlets then left outlets, ascending, no walkways
    const merged = [...t2, ...l2, ...r2].sort((a, b) => a.n - b.n).map(it => ({ type: 'outlet', it }));
    return { topCells: [], leftCells: merged, rightCells: [], overflow: o2, unplaced, count: rs.length };
  }
  return {
    topCells: [...t2].sort((a, b) => a.n - b.n).map(it => ({ type: 'outlet', it })),
    leftCells: columnCells(l2, { walkways: cfg.walkways, desc: false }),
    rightCells: columnCells(r2, { walkways: cfg.walkways, desc: true }),
    overflow: o2, unplaced, count: rs.length,
  };
}

// ── resolve a scanned/typed address across ALL routes ───────────────────────────
function resolveAddress(routes, rawStreet, num) {
  if (streetCanon(rawStreet).replace(/[^A-Z]/g, '').length < 3) return { status: 'no', name: rawStreet, num };

  // rangeByLogin: the number lands inside this driver's range/point — the precise owner.
  // wholeByLogin: the driver lists the street with NO number at all (a true catch-all like "George Street").
  // needNum:      the driver has a numbered entry but we don't have a house number yet.
  const rangeByLogin = new Map(), wholeByLogin = new Map(), needNum = new Map();
  (routes || []).forEach(rt => (rt.streets || []).forEach(entry => {
    if (!sameStreetAs(entry, rawStreet)) return;
    const ranges = entryBands(entry);
    const rec = { rt, entry, ranges };
    if (!ranges.length) { if (!wholeByLogin.has(rt.login)) wholeByLogin.set(rt.login, rec); return; }
    if (num == null) { if (!needNum.has(rt.login)) needNum.set(rt.login, rec); return; }
    if (ranges.some(([lo, hi]) => inRange(num, lo, hi))) rangeByLogin.set(rt.login, rec);
  }));

  const label = streetNameOf(rawStreet) || rawStreet;
  const rangeHits = [...rangeByLogin.values()];
  const wholeHits = [...wholeByLogin.values()];

  // A house number that falls inside a driver's explicit range is the direct hit and
  // must rank first — above any driver who only lists the street with no number.
  if (num != null && rangeHits.length) {
    const others = [...rangeHits.slice(1), ...wholeHits];
    return others.length
      ? { status: 'ok', name: label, num, rec: rangeHits[0], multi: [rangeHits[0], ...others] }
      : { status: 'ok', name: label, num, rec: rangeHits[0] };
  }

  // No explicit range owns the number → fall back to whole-street (catch-all) claimants.
  if (wholeHits.length === 1) return { status: 'ok', name: label, num, rec: wholeHits[0] };
  if (wholeHits.length > 1) return { status: 'ok', name: label, num, rec: wholeHits[0], multi: wholeHits };
  if (needNum.size) return { status: 'ask', name: label, num, options: [...needNum.values()] };

  // street found but the number sits in no range?
  const ranged = new Map();
  (routes || []).forEach(rt => (rt.streets || []).forEach(entry => {
    const bands = entryBands(entry);
    if (sameStreetAs(entry, rawStreet) && bands.length && !ranged.has(rt.login))
      ranged.set(rt.login, { rt, entry, ranges: bands });
  }));
  if (ranged.size && num != null) return { status: 'outofrange', name: label, num, options: [...ranged.values()] };

  // Nothing holds this street. If a route list carries a near-identical spelling
  // ("Bride rd" for Bridge Rd, live on a Broadway round), offer it as a QUESTION —
  // never as a match: a misspelling on one round and a real street on another look
  // identical to a matcher, and only a person can tell them apart.
  const near = [];
  (routes || []).forEach(rt => (rt.streets || []).forEach(entry => {
    if (looksLikeStreet(entry, rawStreet) && !near.some(n => n.rt.login === rt.login)) near.push({ rt, entry });
  }));
  return { status: 'no', name: label, num, near: near.slice(0, 3) };
}

// ── outlet tile ─────────────────────────────────────────────────────────────────
function Tile({ cell, selected, onSelect }) {
  if (cell.type === 'walk') {
    return (
      <div title="Empty slot — walk-through" style={{
        width: 70, height: 58, borderRadius: 9, border: `1px dashed ${C.border}`,
        background: 'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(148,163,184,0.10) 5px, rgba(148,163,184,0.10) 10px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '0.5rem', fontWeight: 700, letterSpacing: '0.1em', color: C.dim, textTransform: 'uppercase', opacity: 0.7,
      }}>walk</div>
    );
  }
  const { rt, n } = cell.it;
  const missort = isMissort(rt);
  return (
    <button onClick={() => onSelect(cell.it)} title={`Outlet ${rt.outlet} — ${rt.login}`} style={{
      width: 70, height: 58, borderRadius: 9, cursor: 'pointer', position: 'relative',
      border: selected ? '2px solid var(--hp-gold)' : `2px solid ${missort ? '#b45309' : 'rgba(120,130,0,0.6)'}`,
      background: missort ? 'rgba(180,83,9,0.9)' : C.yellow,
      color: missort ? '#fff' : C.yellowInk,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      lineHeight: 1.05, fontFamily: 'var(--font-body)', fontWeight: 800,
      boxShadow: selected ? '0 0 0 3px rgba(201,168,76,0.25)' : 'none',
    }}>
      <span style={{ fontSize: '1.15rem', fontVariantNumeric: 'tabular-nums' }}>{n}</span>
      <span style={{ fontSize: '0.52rem', fontWeight: 700, maxWidth: 66, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {missort ? 'MISSORT' : rt.login}
      </span>
    </button>
  );
}

// ── main tab ─────────────────────────────────────────────────────────────────────
export default function ChuteLayoutTab({ sorter }) {
  const [routes, setRoutes] = useState(null);
  const [err, setErr] = useState('');
  const [chuteId, setChuteId] = useState('6');
  const [selected, setSelected] = useState(null); // { n, rt }
  const [scanOpen, setScanOpen] = useState(false);
  const [result, setResult] = useState(null);      // resolveAddress output

  useEffect(() => {
    let live = true;
    callAction('nsGetSortGuide', {})
      .then(r => { if (!live) return; if (r && r.success) setRoutes(r.routes || []); else { setErr((r && r.error) || 'Could not load routes.'); setRoutes([]); } })
      .catch(() => { if (live) { setErr('Could not load routes.'); setRoutes([]); } });
    return () => { live = false; };
  }, []);

  const cfg = CHUTES.find(c => c.id === chuteId);
  const frame = useMemo(() => (routes ? buildFrame(routes, cfg) : null), [routes, cfg]);

  // keep the detail panel in sync when switching chutes
  useEffect(() => { setSelected(null); }, [chuteId]);

  if (routes === null) return <div style={{ ...card, textAlign: 'center', padding: '40px 20px' }}><Loader2 size={22} style={{ animation: 'spin 0.8s linear infinite', color: C.dim }} /></div>;

  const pill = (bg, fg, txt) => <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: '0.72rem', fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: bg, color: fg }}>{txt}</span>;

  return (
    <>
      <div style={card}>
        <h2 style={title}>Chute Layout</h2>
        <p style={sub}>Tap an outlet to see its driver, bay and streets — or scan a label and the screen tells you exactly where it drops.</p>

        {/* Chute switcher */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          {CHUTES.map(c => (
            <button key={c.id} onClick={() => setChuteId(c.id)} style={{
              flex: 1, minWidth: 92, padding: '9px 8px', borderRadius: 10, cursor: 'pointer', fontFamily: 'var(--font-body)',
              border: `1px solid ${chuteId === c.id ? 'var(--hp-gold)' : C.border}`,
              background: chuteId === c.id ? 'rgba(201,168,76,0.14)' : C.card,
              color: chuteId === c.id ? 'var(--hp-gold)' : C.dim, fontWeight: 800,
            }}>
              <div style={{ fontSize: '0.92rem' }}>{c.label}</div>
              <div style={{ fontSize: '0.62rem', fontWeight: 600, opacity: 0.85, marginTop: 2 }}>{c.blurb}</div>
            </button>
          ))}
        </div>

        <button onClick={() => setScanOpen(true)} style={{
          width: '100%', padding: '14px', borderRadius: 12, border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)',
          background: 'linear-gradient(180deg, #35c07a, #1f9d5b)', color: '#0d2410', fontWeight: 800, fontSize: '0.98rem',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9,
        }}>
          <ScanLine size={18} /> Scan a label
        </button>
        {err && <div style={{ marginTop: 12, ...{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: 'rgba(239,68,68,0.12)', color: 'var(--hp-red, #EF4444)' } }}><AlertTriangle size={13} /> {err}</div>}
      </div>

      {/* Board */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
          <span style={{ ...title, fontSize: '1rem' }}>{cfg.label} — sort frame</span>
          <span style={{ fontSize: '0.72rem', color: C.dim }}>{frame.count} outlet{frame.count === 1 ? '' : 's'}</span>
        </div>

        {frame.count === 0 ? (
          <p style={sub}>No outlets are assigned to {cfg.label} yet. Set each round's Chute + Outlet on Driver Routes → Edit codes.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, overflowX: 'auto' }}>
            {frame.topCells.length > 0 && (
              <div style={{ display: 'flex', gap: 8 }}>
                {frame.topCells.map((cell, i) => <Tile key={'t' + i} cell={cell} selected={selected && cell.type === 'outlet' && selected.rt.login === cell.it.rt.login} onSelect={setSelected} />)}
              </div>
            )}
            <div style={{ display: 'flex', gap: 12, alignItems: 'stretch', justifyContent: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {frame.leftCells.map((cell, i) => <Tile key={'l' + i} cell={cell} selected={selected && cell.type === 'outlet' && selected.rt.login === cell.it.rt.login} onSelect={setSelected} />)}
              </div>
              <div style={{
                flex: '0 0 74px', alignSelf: 'stretch', borderRadius: 12, minHeight: 120,
                background: 'linear-gradient(180deg, #145279, #0c3350)', color: '#eaf3fb',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontWeight: 800, letterSpacing: '0.14em', fontSize: '1rem',
              }}>CHUTE {cfg.id}</div>
              {cfg.sides === 'left' ? (
                <div style={{
                  flex: '0 0 108px', alignSelf: 'stretch', borderRadius: 12, border: `1px dashed ${C.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 8,
                  color: C.dim, fontSize: '0.7rem', fontWeight: 700, lineHeight: 1.4,
                }}>Other<br />operator</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {frame.rightCells.map((cell, i) => <Tile key={'r' + i} cell={cell} selected={selected && cell.type === 'outlet' && selected.rt.login === cell.it.rt.login} onSelect={setSelected} />)}
                </div>
              )}
            </div>

            {frame.overflow.length > 0 && (
              <div style={{ width: '100%', borderTop: `1px solid ${C.border}`, paddingTop: 10, marginTop: 2 }}>
                <div style={{ fontSize: '0.62rem', fontWeight: 700, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Overflow outlets</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {frame.overflow.map((it, i) => <Tile key={'o' + i} cell={{ type: 'outlet', it }} selected={selected && selected.rt.login === it.rt.login} onSelect={setSelected} />)}
                </div>
              </div>
            )}
          </div>
        )}

        {frame.unplaced.length > 0 && (
          <div style={{ marginTop: 14, padding: '10px 12px', borderRadius: 10, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)' }}>
            <div style={{ fontSize: '0.72rem', color: 'var(--hp-amber, #f59e0b)', fontWeight: 700, marginBottom: 4 }}>
              {frame.unplaced.length} round{frame.unplaced.length === 1 ? '' : 's'} on {cfg.label} with no outlet number yet
            </div>
            <div style={{ fontSize: '0.72rem', color: C.dim }}>{frame.unplaced.map(r => r.login).join(', ')} — set the Outlet on Driver Routes → Edit codes to place them.</div>
          </div>
        )}
      </div>

      {/* Detail */}
      {selected && (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
            <span style={{ fontSize: '1.5rem', fontWeight: 800 }}>Outlet {selected.rt.outlet}</span>
            {pill(guidePillBg('chute'), C.gold, `Chute ${selected.rt.chute || '—'}`)}
            {pill(guidePillBg('bay'), C.pink, `Bay ${selected.rt.bay || '—'}`)}
          </div>
          <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 14 }}>
            {[['Driver', selected.rt.login], ['Round', selected.rt.dNumber || '—'], ['Area', selected.rt.area || '—'], ['Streets', (selected.rt.streets || []).length]].map(([k, v]) => (
              <div key={k} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '9px 10px' }}>
                <div style={{ fontSize: '0.6rem', fontWeight: 700, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{k}</div>
                <div style={{ fontSize: '0.98rem', fontWeight: 800, marginTop: 2 }}>{v}</div>
              </div>
            ))}
          </div>
          <VerifyStreet rt={selected.rt} />
        </div>
      )}

      {scanOpen && (
        <ScanModal routes={routes} onClose={() => setScanOpen(false)} onResult={r => { setScanOpen(false); setResult(r); }} />
      )}
      {result && <ResultOverlay result={result} onScanAgain={() => { setResult(null); setScanOpen(true); }} onClose={() => setResult(null)} onEnterNumber={(n) => setResult({ ...resolveAddress(routes, result.name, n), scanned: result.scanned })} />}
    </>
  );
}

function guidePillBg(kind) {
  return kind === 'chute' ? 'rgba(245,158,11,0.14)' : kind === 'bay' ? 'rgba(236,72,153,0.14)' : 'rgba(56,189,248,0.14)';
}

// verify a street belongs to the selected outlet
function VerifyStreet({ rt }) {
  const [q, setQ] = useState('');
  const streets = rt.streets || [];
  // Typing is forgiving forwards ("common" finds Commonwealth St) but never
  // sideways: "Bridge Rd" must not tick green on a round that runs Pyrmont
  // Bridge Rd — that is exactly how a parcel ends up on the wrong outlet.
  const hitIdx = q.trim().length >= 2
    ? streets.findIndex(s => matchStreet(s, q, { allowPartial: true }) !== null)
    : -1;
  return (
    <div>
      <div style={{ fontSize: '0.6rem', fontWeight: 700, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 5 }}>Verify a street belongs here</div>
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="Type the parcel's street…" autoCapitalize="none" style={{
        width: '100%', background: C.card, border: `1px solid ${C.border}`, borderRadius: 9, color: C.text, fontSize: '0.9rem', padding: '9px 11px', boxSizing: 'border-box', fontFamily: 'var(--font-body)',
      }} />
      {q.trim() && (
        <div style={{ marginTop: 8, fontSize: '0.82rem', fontWeight: 700, color: hitIdx >= 0 ? '#22c55e' : 'var(--hp-red, #EF4444)' }}>
          {hitIdx >= 0 ? `✓ Belongs on Outlet ${rt.outlet}` : `✗ Not on Outlet ${rt.outlet}`}
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
        {streets.map((s, i) => (
          <span key={i} style={{ fontSize: '0.76rem', padding: '4px 8px', borderRadius: 6, border: `1px solid ${C.border}`,
            background: i === hitIdx ? 'rgba(34,197,94,0.18)' : C.card, color: i === hitIdx ? '#22c55e' : C.text, fontWeight: i === hitIdx ? 700 : 400 }}>{s}</span>
        ))}
      </div>
    </div>
  );
}

// ── scan modal: camera + OCR, with a type-it fallback ───────────────────────────
function ScanModal({ routes, onClose, onResult }) {
  const [img, setImg] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [manual, setManual] = useState('');

  const runOcr = async (dataUrl) => {
    setImg(dataUrl); setErr(''); setBusy(true);
    try {
      const r = await fetch('/.netlify/functions/ocr-sort-label', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageBase64: dataUrl.split(',')[1], mediaType: 'image/jpeg' }),
      }).then(x => x.json());
      if (r && r.success) onResult({ ...resolveAddress(routes, r.street, r.number), scanned: r });
      else { setErr('Could not read the address — try again, or type it in.'); }
    } catch { setErr('Scan failed. Check your connection or type the address in.'); }
    finally { setBusy(false); }
  };

  const runManual = () => {
    const v = manual.trim(); if (!v) return;
    const numM = v.match(/\b(\d{1,5})\b/);
    onResult(resolveAddress(routes, v, numM ? parseInt(numM[1], 10) : null));
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 520, background: C.surface, borderTopLeftRadius: 18, borderTopRightRadius: 18, border: `1px solid ${C.border}`, padding: 20, maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ ...title, margin: 0, fontSize: '1.1rem' }}>Scan a label</span>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: C.dim, cursor: 'pointer' }}><X size={20} /></button>
        </div>
        <p style={sub}>Point the camera at the delivery address. It reads the street and tells you where it drops.</p>

        <CameraCapture nativeFirst guide="document" maxEdge={1280} quality={0.8} value={img} onCapture={runOcr} onClear={() => setImg('')} />

        {busy && <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, color: C.dim, fontSize: '0.84rem' }}><Loader2 size={16} style={{ animation: 'spin 0.8s linear infinite' }} /> Reading label…</div>}
        {err && <div style={{ marginTop: 12, fontSize: '0.8rem', color: 'var(--hp-red, #EF4444)', fontWeight: 600 }}>{err}</div>}

        <div style={{ borderTop: `1px solid ${C.border}`, margin: '16px 0 12px', paddingTop: 14 }}>
          <div style={{ fontSize: '0.6rem', fontWeight: 700, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Or type the address</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={manual} onChange={e => setManual(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') runManual(); }} placeholder="e.g. 142 Commonwealth Street" autoCapitalize="none" style={{
              flex: 1, background: C.card, border: `1px solid ${C.border}`, borderRadius: 9, color: C.text, fontSize: '0.9rem', padding: '9px 11px', fontFamily: 'var(--font-body)',
            }} />
            <button onClick={runManual} style={{ background: 'var(--hp-gold)', color: '#000', border: 'none', borderRadius: 9, padding: '0 16px', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}><CornerDownLeft size={15} /> Find</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── full-screen sort-to result ──────────────────────────────────────────────────
function ResultOverlay({ result, onScanAgain, onClose, onEnterNumber }) {
  const onNum = onEnterNumber;
  const [askVal, setAskVal] = useState(result.num != null ? String(result.num) : '');
  const rangeText = ranges => ranges.map(([lo, hi]) => (lo === hi ? `${lo}` : `${lo}–${hi}`)).join(', ');
  let bg, body;

  if (result.status === 'ok') {
    const rt = result.rec.rt;
    bg = 'linear-gradient(160deg, #22a35f, #157f47)';
    const ranges = result.rec.ranges || [];
    const hasOutlet = rt.outlet && rt.outlet !== '—';
    const bigVal = hasOutlet ? rt.outlet : (rt.bay || rt.login);
    const kicker = hasOutlet ? 'Sort to outlet' : (rt.bay ? 'Sort to bay' : 'Sort to');
    body = (
      <>
        <div style={rsKicker}>{kicker}</div>
        <div style={rsOutlet}>{bigVal}</div>
        <div style={rsRoute}>Chute {rt.chute} <span style={{ opacity: 0.7 }}>·</span> {hasOutlet ? `Bay ${rt.bay}` : `Outlet —`} <span style={{ opacity: 0.7 }}>·</span> {rt.login}</div>
        <div style={rsAddr}>{result.num != null && <span style={rsNum}>{result.num}</span>} {result.name}</div>
        {ranges.length > 0 && result.num != null && <div style={rsNote}>{result.num} falls in {rangeText(ranges)} → {rt.login}</div>}
        {!hasOutlet && <div style={rsNote}>This round has no outlet number set yet — sort by bay/driver.</div>}
      </>
    );
  } else if (result.status === 'ask' || result.status === 'outofrange') {
    bg = 'linear-gradient(160deg, #c98a1e, #a06a12)';
    body = (
      <>
        <div style={rsKicker}>{result.status === 'outofrange' ? `No. ${result.num} not in range` : 'Which house number?'}</div>
        <div style={{ ...rsOutlet, fontSize: 'clamp(2rem,9vw,4rem)' }}>{result.name}</div>
        <div style={{ ...rsRoute, fontWeight: 700 }}>Shared street — the number decides the driver</div>
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 6, fontSize: '1rem' }}>
          {result.options.map((o, i) => <div key={i}><strong>{o.rt.outlet}</strong> · Bay {o.rt.bay} · {o.rt.login} — {rangeText(o.ranges)}</div>)}
        </div>
        <div style={{ marginTop: 18, display: 'flex', gap: 8 }}>
          <input value={askVal} onChange={e => setAskVal(e.target.value)} inputMode="numeric" placeholder="No." style={{ width: 130, textAlign: 'center', fontSize: '1.5rem', fontWeight: 800, border: 'none', borderRadius: 12, padding: 8 }} />
          <button onClick={() => { const v = parseInt(askVal, 10); if (!isNaN(v) && onNum) onNum(v); }} style={{ fontWeight: 800, fontSize: '1rem', border: '2px solid #fff', background: 'transparent', color: '#fff', borderRadius: 12, padding: '0 18px', cursor: 'pointer' }}>Find</button>
        </div>
      </>
    );
  } else {
    bg = 'linear-gradient(160deg, #cc5136, #9e3620)';
    body = (
      <>
        <div style={rsKicker}>Not found</div>
        <div style={{ ...rsOutlet, fontSize: 'clamp(2rem,9vw,4rem)' }}>{result.num != null && <span style={rsNum}>{result.num}</span>} {result.name || '—'}</div>
        <div style={{ ...rsRoute, fontWeight: 700 }}>No outlet on these chutes holds that street</div>
        <div style={rsNote}>Check the address, or it belongs to another chute.</div>
        {(result.near || []).length > 0 && (
          <div style={{ ...rsNote, marginTop: 10 }}>
            Spelt almost the same on a round — check before you drop it:
            {result.near.map((n, i) => <div key={i} style={{ fontWeight: 700 }}>“{n.entry}” · Outlet {n.rt.outlet || '—'} · {n.rt.login}</div>)}
          </div>
        )}
      </>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: bg, color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 24 }}>
      {result.scanned && result.status !== 'ok' && <div style={{ position: 'absolute', top: 16, fontSize: '0.72rem', opacity: 0.85 }}>Read: “{result.scanned.raw || result.scanned.street}”</div>}
      {body}
      {onNum && Array.isArray(result.scanned?.numbers) && result.scanned.numbers.length >= 2 && (
        <div style={{ marginTop: 20, borderTop: '1px solid rgba(255,255,255,0.25)', paddingTop: 14, maxWidth: 460 }}>
          <div style={{ fontSize: '0.82rem', fontWeight: 700, opacity: 0.9, marginBottom: 10, lineHeight: 1.4 }}>The label has more than one number — which is the street number?</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
            {result.scanned.numbers.map((n) => (
              <button key={n} onClick={() => onNum(n)} style={{
                minWidth: 50, fontSize: '1.25rem', fontWeight: 800, padding: '7px 13px', borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit',
                ...(result.num === n
                  ? { background: '#fff', color: '#111', border: '2px solid #fff' }
                  : { background: 'rgba(255,255,255,0.16)', color: '#fff', border: '2px solid rgba(255,255,255,0.5)' }),
              }}>{n}</button>
            ))}
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 12, marginTop: 30 }}>
        <button onClick={onScanAgain} style={rsAgain}>Scan next</button>
        <button onClick={onClose} style={{ ...rsAgain, background: 'transparent' }}>Close</button>
      </div>
    </div>
  );
}
const rsKicker = { fontSize: '0.95rem', fontWeight: 800, letterSpacing: '0.22em', textTransform: 'uppercase', opacity: 0.9 };
// The outlet number is the whole point of the screen — make it dominate.
const rsOutlet = { fontSize: 'clamp(6rem,38vw,17rem)', fontWeight: 900, lineHeight: 0.85, margin: '10px 0 6px', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.03em' };
const rsRoute = { fontSize: 'clamp(1.2rem,5vw,2rem)', fontWeight: 800 };
const rsAddr = { marginTop: 20, fontSize: 'clamp(1rem,4vw,1.4rem)', fontWeight: 700 };
const rsNum = { background: 'rgba(255,255,255,0.22)', borderRadius: 8, padding: '0 10px' };
const rsNote = { marginTop: 12, fontSize: '1rem', fontWeight: 600, opacity: 0.9 };
const rsAgain = { fontWeight: 800, fontSize: '0.95rem', letterSpacing: '0.08em', textTransform: 'uppercase', border: '2px solid rgba(255,255,255,0.75)', background: 'rgba(255,255,255,0.14)', color: '#fff', borderRadius: 12, padding: '12px 24px', cursor: 'pointer' };
